import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '@/utils/api';
import { openDocument } from '@/utils/documents';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft, CheckCircle2, XCircle, Clock, ExternalLink, FileText, Shield, DollarSign, Users
} from 'lucide-react';

const STATUS_TABS = [
  { value: 'pending',  i18nKey: 'pending',  color: 'bg-amber-100 text-amber-800' },
  { value: 'approved', i18nKey: 'approved', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'rejected', i18nKey: 'rejected', color: 'bg-red-100 text-red-800' },
  { value: '',         i18nKey: 'all',      color: 'bg-gray-100 text-gray-800' }
];

const stripHtml = (s) => {
  if (!s || typeof s !== 'string') return '';
  const clean = s.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  return clean.length > 240 ? clean.slice(0, 240) + '…' : clean;
};

const humanErrorMessage = (e, t) => {
  const status = e?.response?.status;
  const data = e?.response?.data;
  if (status >= 500) {
    return t('adminPurchases.errorServer');
  }
  if (typeof data === 'string') {
    if (data.includes('<') || data.length > 200) {
      return t('adminPurchases.errorServer');
    }
    return data;
  }
  if (data && typeof data === 'object' && typeof data.detail === 'string') {
    return stripHtml(data.detail) || t('adminPurchases.errorLoad');
  }
  return t('adminPurchases.errorLoad');
};

export default function AdminPurchases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionPurchase, setActionPurchase] = useState(null); // {id, action}
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab]);

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [list, st] = await Promise.all([
        adminAPI.listPurchases(tab || undefined),
        adminAPI.getStats()
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch (e) {
      setItems([]);
      setMessage(humanErrorMessage(e, t));
    } finally {
      setLoading(false);
    }
  };

  const submitAction = async () => {
    if (!actionPurchase) return;
    setSubmitting(true);
    setMessage('');
    try {
      if (actionPurchase.action === 'approve') {
        await adminAPI.approvePurchase(actionPurchase.id, note);
      } else {
        await adminAPI.rejectPurchase(actionPurchase.id, note);
      }
      setActionPurchase(null);
      setNote('');
      await load();
    } catch (e) {
      setMessage(humanErrorMessage(e, t));
    } finally {
      setSubmitting(false);
    }
  };

  // Files require a Bearer token — a plain window.open sends no Authorization
  // header, so fetch as an authenticated blob (auto-refreshes an expired
  // access token once via the shared axios instance).
  const openProof = (id) => openDocument(`/admin/purchases/${id}/proof`, `comprovativo_${id.slice(0, 8)}.pdf`);
  const openReceipt = (id) => openDocument(`/purchases/${id}/receipt`, `recibo_${id.slice(0, 8)}.pdf`);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('pt-PT'); } catch { return '—'; }
  };

  const paymentMethodLabel = (m) => (
    m === 'vinti4' ? 'Vinti4'
    : m === 'stripe' ? 'Stripe'
    : m === 'bank_transfer' ? t('adminPurchases.bankTransfer')
    : (m || t('adminPurchases.payment'))
  );

  if (user && user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <Shield className="h-7 w-7 text-blue-600" />
            <h1 className="text-3xl font-bold">{t('adminPurchases.title')}</h1>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/admin/jobs')}
            data-testid="admin-go-jobs"
          >
            {t('adminPurchases.jobModeration')}
          </Button>
        </div>
        <p className="text-gray-600 mb-8">{t('adminPurchases.subtitle')}</p>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Clock} label={t('adminPurchases.pending')} value={stats.pending} color="amber" />
            <StatCard icon={CheckCircle2} label={t('adminPurchases.approved')} value={stats.approved} color="emerald" />
            <StatCard icon={XCircle} label={t('adminPurchases.rejected')} value={stats.rejected} color="red" />
            <StatCard icon={DollarSign} label={t('adminPurchases.totalRevenue')} value={stats.total_revenue_cve?.toLocaleString('pt-PT') || 0} color="blue" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {STATUS_TABS.map(tb => (
            <button
              key={tb.value}
              onClick={() => setTab(tb.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === tb.value ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`admin-tab-${tb.value || 'all'}`}
            >
              {t(`adminPurchases.tab.${tb.i18nKey}`)}
            </button>
          ))}
        </div>

        {message && <Alert variant="destructive" className="mb-4"><AlertDescription>{message}</AlertDescription></Alert>}

        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('adminPurchases.loading')}</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{t('adminPurchases.noPurchases', { status: tab || '' })}</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map(p => (
              <Card key={p.id} className="p-5" data-testid={`admin-purchase-${p.invoice_number}`}>
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-gray-500">#{p.invoice_number}</span>
                      <StatusBadge status={p.status} t={t} />
                    </div>
                    <div className="text-lg font-semibold mt-1">{p.plan_name} · {p.amount_cve.toLocaleString('pt-PT')} CVE</div>
                    <div className="text-sm text-gray-600">
                      <Users className="h-3 w-3 inline mr-1" />
                      <strong>{p.employer_company || p.employer_email}</strong> ({p.employer_email})
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {paymentMethodLabel(p.payment_method)} · Ref: <code>{p.bank_reference}</code> · {formatDate(p.created_at)}
                    </div>
                    {p.admin_note && <div className="text-xs text-gray-600 mt-1"><strong>{t('adminPurchases.note')}:</strong> {stripHtml(p.admin_note)}</div>}
                  </div>
                  <div className="flex flex-col gap-2">
                    {p.proof_filename && (
                      <Button variant="outline" size="sm" onClick={() => openProof(p.id)} data-testid={`view-proof-${p.invoice_number}`}>
                        <FileText className="h-4 w-4 mr-1" />{t('adminPurchases.proof')}<ExternalLink className="h-3 w-3 ml-1" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => openReceipt(p.id)}>
                      <FileText className="h-4 w-4 mr-1" />{t('adminPurchases.invoice')}
                    </Button>
                    {p.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => setActionPurchase({ id: p.id, action: 'approve', invoice: p.invoice_number })}
                          data-testid={`approve-btn-${p.invoice_number}`}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />{t('adminPurchases.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setActionPurchase({ id: p.id, action: 'reject', invoice: p.invoice_number })}
                          data-testid={`reject-btn-${p.invoice_number}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />{t('adminPurchases.reject')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Action modal */}
      {actionPurchase && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="admin-action-modal">
          <Card className="p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-1">
              {actionPurchase.action === 'approve' ? t('adminPurchases.approvePurchase') : t('adminPurchases.rejectPurchase')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{t('adminPurchases.invoiceNumber', { number: actionPurchase.invoice })}</p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={actionPurchase.action === 'approve' ? t('adminPurchases.notePlaceholderApprove') : t('adminPurchases.notePlaceholderReject')}
              rows={4}
              data-testid="admin-action-note"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setActionPurchase(null); setNote(''); }}>{t('adminPurchases.cancel')}</Button>
              <Button
                onClick={submitAction}
                disabled={submitting || (actionPurchase.action === 'reject' && !note.trim())}
                className={actionPurchase.action === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="admin-confirm-action-btn"
              >
                {submitting ? t('adminPurchases.processing') : t('adminPurchases.confirm')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const map = {
    amber:   'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    red:     'bg-red-100 text-red-700',
    blue:    'bg-blue-100 text-blue-700'
  };
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
          <div className="text-2xl font-bold mt-1">{value}</div>
        </div>
        <div className={`p-3 rounded-lg ${map[color]}`}><Icon className="h-5 w-5" /></div>
      </div>
    </Card>
  );
}

function StatusBadge({ status, t }) {
  const map = {
    pending:  { label: t('adminPurchases.statusPending'),  cls: 'bg-amber-100 text-amber-800' },
    approved: { label: t('adminPurchases.statusApproved'),  cls: 'bg-emerald-100 text-emerald-800' },
    rejected: { label: t('adminPurchases.statusRejected'), cls: 'bg-red-100 text-red-800' },
    expired:  { label: t('adminPurchases.statusExpired'),  cls: 'bg-gray-100 text-gray-800' }
  };
  const cfg = map[status] || map.pending;
  return <Badge className={cfg.cls}>{cfg.label}</Badge>;
}
